import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RolesContext = createContext(null);

/**
 * Provides the list of dynamic role definitions from GET /api/roles.
 * Any authenticated user can list roles (needed for dropdowns/badges).
 *
 * Shape per role:
 *   { id, key, label, description, color, rank, order,
 *     capabilities: { [cap_key]: bool },
 *     flags: { is_hr_approver, is_manager_owner },
 *     creatable_by: [role_key], protected }
 */
export function RolesProvider({ children }) {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setRoles([]); setLoaded(true); return; }
    try {
      const r = await api.get("/roles");
      setRoles(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRoles([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const byKey = useCallback((key) => roles.find(r => r.key === key), [roles]);
  const label = useCallback((key) => byKey(key)?.label || key, [byKey]);
  const color = useCallback((key) => byKey(key)?.color || "#0A0A0A", [byKey]);
  const rank = useCallback((key) => byKey(key)?.rank ?? 0, [byKey]);
  const maxRank = useCallback((keys) => {
    if (!keys || keys.length === 0) return 0;
    return Math.max(...keys.map(k => rank(k)));
  }, [rank]);

  return (
    <RolesContext.Provider value={{ roles, loaded, refresh, byKey, label, color, rank, maxRank }}>
      {children}
    </RolesContext.Provider>
  );
}

export function useRoles() {
  const ctx = useContext(RolesContext);
  if (!ctx) {
    return { roles: [], loaded: false, refresh: () => {}, byKey: () => null, label: (k) => k, color: () => "#0A0A0A", rank: () => 0, maxRank: () => 0 };
  }
  return ctx;
}
